import Decimal from 'decimal.js';

// Indonesian formatting per §6.9 of the brief.
const idIDNumber = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const idIDMoney  = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const idIDDate   = new Intl.DateTimeFormat('id-ID', { year: 'numeric', month: 'short', day: '2-digit' });
const idIDDateLong = new Intl.DateTimeFormat('id-ID', { year: 'numeric', month: 'long', day: '2-digit' });
const idIDDateTime = new Intl.DateTimeFormat('id-ID', {
  year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
});

/** Format a money amount; default currency = IDR with 'Rp ' prefix. */
export function money(value: string | number | null | undefined, currency = 'IDR'): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Decimal(typeof value === 'number' ? value.toString() : value);
  const formatted = idIDMoney.format(d.toNumber());
  if (currency === 'IDR') return `Rp ${formatted}`;
  return `${currency} ${formatted}`;
}

/** Compact money for KPI tiles so values from Rp 0 to Rp 16 B fit one line:
 *  Rp 16,63 M · Rp 429,27 jt · Rp 850 rb. Use money() for the exact figure
 *  (e.g. in a title/tooltip). Indonesian magnitude suffixes: rb/jt/M/T. */
export function moneyCompact(value: string | number | null | undefined, currency = 'IDR'): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = new Decimal(typeof value === 'number' ? value.toString() : value).toNumber();
  const prefix = currency === 'IDR' ? 'Rp ' : `${currency} `;
  const abs = Math.abs(n);
  const compact = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  if (abs >= 1e12) return `${prefix}${compact.format(n / 1e12)} T`;
  if (abs >= 1e9) return `${prefix}${compact.format(n / 1e9)} M`;
  if (abs >= 1e6) return `${prefix}${compact.format(n / 1e6)} jt`;
  if (abs >= 1e3) return `${prefix}${compact.format(n / 1e3)} rb`;
  return `${prefix}${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(n)}`;
}

/** Format a quantity / non-money number. */
export function num(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Decimal(typeof value === 'number' ? value.toString() : value);
  return idIDNumber.format(d.toNumber());
}

/** Format an ISO date string as Indonesian short date. */
export function date(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return idIDDate.format(d);
}

export function dateLong(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return idIDDateLong.format(d);
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return idIDDateTime.format(d);
}

/** Truncate a long string with an ellipsis. */
export function truncate(s: string, n = 50): string {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
}
